// Core
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Chip, Crumbs, DataTable, KpiStrip, PageHead, Segmented, Toggle } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetAllRespByCommunityIdQuery,
  useUpdateRespByIdMutation,
} from "state/resp/respApiSlice";
import { useGetMeters } from "utils/crud/read";

/* The design prototype shows one RESP toggle per meter, but the real model is
   a pair of meters (provider → receiver) with its own is_active flag — so each
   table row is one connection, keeping the design's table + switch look. */
function Resp() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["resp", "community"]);

  const [statusFilter, setStatusFilter] = useState("ALL");

  // READ resp
  const { data: allRespData } = useGetAllRespByCommunityIdQuery(community.id);
  const meters = useGetMeters("true");

  const meterIdToIdentifier = (meterId) =>
    meters?.find((meter) => meter.id === meterId)?.identifier ?? meterId;

  const respList = useMemo(
    () =>
      allRespData
        ? Object.values(allRespData.entities).map((resp) => ({
            ...resp,
            providerIdentifier: meterIdToIdentifier(resp.provider_meter_id),
            receiverIdentifier: meterIdToIdentifier(resp.receiver_meter_id),
          }))
        : [],
    [allRespData, meters]
  );

  // UPDATE resp — silent toggle (no success modal, only shows errors)
  const [updateResp] = useUpdateRespByIdMutation();
  const onToggle = async (resp) => {
    try {
      await updateResp({
        communityId: community.id,
        assetId: resp.id,
        newAsset: { ...resp, is_active: !resp.is_active },
      }).unwrap();
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  const rows = useMemo(
    () =>
      respList
        .filter(
          (resp) =>
            statusFilter === "ALL" || (statusFilter === "ACTIVE" ? resp.is_active : !resp.is_active)
        )
        .map((resp) => ({
          id: resp.id,
          provider: resp.providerIdentifier,
          receiver: resp.receiverIdentifier,
          active: !!resp.is_active,
          respEntity: resp,
        })),
    [respList, statusFilter]
  );

  const meterCell = (identifier) => (
    <div className="rec-cell-member">
      <div
        className="rec-av-sm"
        style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}
      >
        <Icon name="electric_meter" style={{ fontSize: 18 }} />
      </div>
      <span className="font-[family-name:var(--mono)] text-[12.5px] text-ink">{identifier}</span>
    </div>
  );

  const columns = [
    {
      key: "provider",
      label: t("table.columns.provider"),
      render: (v) => meterCell(v),
    },
    {
      key: "_arrow",
      label: "",
      sort: false,
      width: 40,
      render: () => <Icon name="arrow_right_alt" style={{ fontSize: 20, color: "var(--ink-3)" }} />,
    },
    {
      key: "receiver",
      label: t("table.columns.receiver"),
      render: (v) => meterCell(v),
    },
    {
      key: "active",
      label: t("table.columns.is_active"),
      align: "right",
      render: (v, row) => (
        <div className="inline-flex items-center gap-[10px]">
          <span
            className="text-[12.5px] font-semibold"
            style={{ color: v ? "var(--success)" : "var(--ink-3)" }}
          >
            {v ? t("table.options.yes") : t("table.options.no")}
          </span>
          <Toggle
            on={v}
            ariaLabel={t("table.columns.is_active")}
            onClick={() => onToggle(row.respEntity)}
          />
        </div>
      ),
    },
  ];

  const activeCount = respList.filter((r) => r.is_active).length;
  const metersInvolved = new Set(
    respList.flatMap((r) => [r.provider_meter_id, r.receiver_meter_id])
  ).size;

  const statusOptions = [
    { value: "ALL", label: t("filters.all") },
    { value: "ACTIVE", label: t("filters.active") },
    { value: "INACTIVE", label: t("filters.inactive") },
  ];

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="resp" />
      <Crumbs
        items={[
          { label: community?.name || "—" },
          {
            label: t("sidenav.infrastructure", { ns: "community", defaultValue: "Infraestrutura" }),
          },
          { label: t("title") },
        ]}
      />
      <PageHead title={t("title")} subtitle={t("subtitle")} />

      <KpiStrip
        className="mb-rec"
        cols={4}
        items={[
          { icon: "lan", value: String(respList.length), label: t("stats.total"), tint: "teal" },
          {
            icon: "check_circle",
            value: String(activeCount),
            label: t("stats.active"),
            tint: "green",
          },
          {
            icon: "power_settings_new",
            value: String(respList.length - activeCount),
            label: t("stats.inactive"),
            tint: "amber",
          },
          {
            icon: "electric_meter",
            value: String(metersInvolved),
            label: t("stats.meters"),
            tint: "info",
          },
        ]}
      />

      <div className="rec-card">
        <DataTable
          columns={columns}
          rows={rows}
          search={t("search_placeholder")}
          searchKeys={["provider", "receiver"]}
          emptyText={t("empty")}
          filters={
            <div className="flex items-center gap-2">
              <Chip tone="teal">{`${activeCount}/${respList.length}`}</Chip>
              <Segmented options={statusOptions} value={statusFilter} onChange={setStatusFilter} />
            </div>
          }
        />
      </div>
    </div>
  );
}

export default Resp;
